import { FC, useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  IconButton,
  Alert,
  Box,
  CircularProgress,
  Paper,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import { contentApi, csvApi } from '../services/api';

interface CategoryRow {
  category: string;
  parent: string;
  idnumber: string;
  description: string;
}

interface CategoriesEditorProps {
  open: boolean;
  onClose: () => void;
  onSaveSuccess: () => void;
}

export const CategoriesEditor: FC<CategoriesEditorProps> = ({
  open,
  onClose,
  onSaveSuccess,
}) => {
  const [rows, setRows] = useState<CategoryRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      loadCategories();
    }
  }, [open]);

  const loadCategories = async () => {
    setLoading(true);
    setError(null);
    try {
      // Try to load existing categories.csv
      const data = await csvApi.parseCSV('categories', 'categories.csv');
      const categoryRows: CategoryRow[] = data.rows.map((row: any) => ({
        category: row.category || '',
        parent: row.parent || '',
        idnumber: row.idnumber || '',
        description: row.description || '',
      }));
      setRows(categoryRows);
    } catch (err) {
      // File doesn't exist yet, start with empty form
      console.log('No existing categories.csv, starting fresh');
      setRows([]);
    }
    setLoading(false);
  };

  const handleAddRow = () => {
    setRows([
      ...rows,
      {
        category: '',
        parent: '',
        idnumber: '',
        description: '',
      },
    ]);
  };

  const handleDeleteRow = (index: number) => {
    setRows(rows.filter((_, i) => i !== index));
  };

  const handleRowChange = (
    index: number,
    field: keyof CategoryRow,
    value: string
  ) => {
    const newRows = [...rows];
    newRows[index] = { ...newRows[index], [field]: value };
    setRows(newRows);
  };

  const handleSave = async () => {
    // Filter out empty rows
    const filledRows = rows.filter((row) => row.category.trim());

    if (filledRows.length === 0) {
      setError('At least one category is required');
      return;
    }

    setSaving(true);
    setError(null);

    try {
      const headers = ['category', 'parent', 'idnumber', 'description'];
      await csvApi.updateCSV('categories', 'categories.csv', {
        headers,
        rows: filledRows as any,
      });

      onSaveSuccess();
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save categories');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>Edit Categories</DialogTitle>
      <DialogContent sx={{ pt: 2 }}>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            <Box sx={{ mb: 2 }}>
              <Button
                startIcon={<AddIcon />}
                onClick={handleAddRow}
                variant="outlined"
                disabled={saving}
              >
                Add Category
              </Button>
            </Box>

            <Paper variant="outlined" sx={{ overflow: 'auto', maxHeight: 400 }}>
              <Table size="small">
                <TableHead sx={{ bgcolor: 'action.hover' }}>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 'bold', width: '25%' }}>
                      Category Name *
                    </TableCell>
                    <TableCell sx={{ fontWeight: 'bold', width: '20%' }}>
                      Parent Category
                    </TableCell>
                    <TableCell sx={{ fontWeight: 'bold', width: '15%' }}>
                      ID Number
                    </TableCell>
                    <TableCell sx={{ fontWeight: 'bold', width: '30%' }}>
                      Description
                    </TableCell>
                    <TableCell sx={{ width: '10%', textAlign: 'center' }}>
                      Action
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rows.map((row, index) => (
                    <TableRow key={index}>
                      <TableCell>
                        <TextField
                          size="small"
                          value={row.category}
                          onChange={(e) =>
                            handleRowChange(index, 'category', e.target.value)
                          }
                          placeholder="e.g., Science"
                          disabled={saving}
                          fullWidth
                        />
                      </TableCell>
                      <TableCell>
                        <TextField
                          size="small"
                          value={row.parent}
                          onChange={(e) =>
                            handleRowChange(index, 'parent', e.target.value)
                          }
                          placeholder="e.g., STEM (leave empty for root)"
                          disabled={saving}
                          fullWidth
                        />
                      </TableCell>
                      <TableCell>
                        <TextField
                          size="small"
                          value={row.idnumber}
                          onChange={(e) =>
                            handleRowChange(index, 'idnumber', e.target.value)
                          }
                          placeholder="e.g., SCI-001"
                          disabled={saving}
                          fullWidth
                        />
                      </TableCell>
                      <TableCell>
                        <TextField
                          size="small"
                          value={row.description}
                          onChange={(e) =>
                            handleRowChange(index, 'description', e.target.value)
                          }
                          placeholder="Optional description"
                          disabled={saving}
                          fullWidth
                        />
                      </TableCell>
                      <TableCell sx={{ textAlign: 'center' }}>
                        <IconButton
                          size="small"
                          onClick={() => handleDeleteRow(index)}
                          disabled={saving}
                          color="error"
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Paper>

            <Box sx={{ mt: 2, fontSize: '0.875rem', color: '#666' }}>
              <p>
                <strong>Field Descriptions:</strong>
              </p>
              <ul>
                <li>
                  <strong>Category Name:</strong> The display name of the category
                  (required)
                </li>
                <li>
                  <strong>Parent Category:</strong> The name of the parent category
                  (leave empty for root level)
                </li>
                <li>
                  <strong>ID Number:</strong> Unique identifier for the category
                </li>
                <li>
                  <strong>Description:</strong> Optional description for the category
                </li>
              </ul>
            </Box>
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving || loading}>
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          disabled={saving || loading || rows.length === 0}
        >
          {saving ? 'Saving...' : 'Save Categories'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CategoriesEditor;
